import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, authedFetch, unwrap } from '@/shared/api/client';
import {
  clearToken,
  decodeToken,
  getToken,
  isExpired,
  setToken,
  type TokenClaims,
} from './token';
import {
  canSeeModule,
  canSeePermission,
  type ModuleId,
  type PermissionId,
} from './modules';

interface AuthContextValue {
  claims: TokenClaims | null;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  can: (moduleId: ModuleId) => boolean;
  canPermission: (permission: PermissionId) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Read and validate whatever is already in storage. */
function currentClaims(): TokenClaims | null {
  const token = getToken();
  if (!token) return null;
  const claims = decodeToken(token);
  if (isExpired(claims)) {
    clearToken();
    return null;
  }
  return claims;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [claims, setClaims] = useState<TokenClaims | null>(currentClaims);

  useEffect(() => {
    if (!claims) return;
    let cancelled = false;
    const refresh = async () => {
      const response = await authedFetch('/api/User/current/access').catch(() => null);
      if (!response?.ok || cancelled) return;
      const access = (await response.json()) as {
        role: string;
        modules: number[];
        permissions: string[];
      };
      setClaims(
        (current) =>
          current && {
            ...current,
            role: access.role,
            modules: access.modules,
            permissions: access.permissions ?? [],
          },
      );
    };
    void refresh();
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [claims?.userId]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await unwrap(api.POST('/api/auth/sign-in', { body: { email, password } }));

    const token = result?.jwt;
    if (!token) {
      throw new Error('Sign-in succeeded but no token was returned.');
    }

    setToken(token);
    const decoded = decodeToken(token);
    if (!decoded) {
      clearToken();
      throw new Error('The server returned a token this app could not read.');
    }
    setClaims(decoded);
  }, []);

  const signOut = useCallback(() => {
    clearToken();
    setClaims(null);
    // Drop every cached response - it belongs to the previous user, and the
    // backend's row-level filtering means the next one may see different rows.
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      claims,
      isAuthenticated: claims !== null,
      signIn,
      signOut,
      can: (moduleId: ModuleId) => canSeeModule(moduleId, claims),
      canPermission: (permission: PermissionId) =>
        canSeePermission(permission, claims),
    }),
    [claims, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
